/**
 * Privacy Policy — placeholder / draft legal notice.
 *
 * Mounted at /privacy and (via alias route) /legal/privacy. Publicly
 * accessible (no auth gate). V2 tokens, light-first, dark-opt-in.
 *
 * NOTE: The body copy in each section is a plain-English stub — enough
 * for a real user to skim today, but explicitly meant to be replaced by
 * counsel-approved language before public launch. The visible banner
 * at the top makes this state obvious to the reader.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

const LAST_UPDATED = new Date().toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

function Section({ id, title, children }) {
  return (
    <section
      id={id}
      data-testid={`legal-section-${id}`}
      className="mt-10 first:mt-0"
    >
      <h2
        className="text-v2-display-sm text-v2-text mb-3"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3 text-v2-body-md text-v2-textMuted leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <main
      data-testid="privacy-policy-page"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-16 md:py-24">
        <Link
          to="/"
          data-testid="privacy-policy-back-home"
          className="inline-flex items-center gap-2 text-v2-caption uppercase tracking-[0.16em] text-v2-textMuted hover:text-v2-accent transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Back to home
        </Link>

        <header className="mb-10">
          <p className="text-v2-caption uppercase tracking-[0.18em] text-v2-textMuted">
            Legal
          </p>
          <h1
            className="mt-2 text-v2-display-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Privacy Policy
          </h1>
          <p
            data-testid="privacy-policy-last-updated"
            className="mt-3 text-v2-body-sm text-v2-textSubtle"
          >
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        {/* Draft banner — meta-copy about the page state, not lorem body. */}
        <div
          data-testid="privacy-policy-draft-banner"
          className="mb-12 rounded-v2-lg p-5 flex items-start gap-3"
          style={{
            background: "var(--v2-warning-soft)",
            border: "1px solid var(--v2-warning)",
          }}
        >
          <AlertTriangle
            className="w-5 h-5 mt-0.5 shrink-0"
            style={{ color: "var(--v2-warning)" }}
            aria-hidden="true"
          />
          <div>
            <div
              className="text-v2-body-md font-semibold"
              style={{ color: "var(--v2-warning)" }}
            >
              This is a temporary legal notice.
            </div>
            <p className="mt-1 text-v2-body-sm text-v2-textMuted">
              Formal Terms of Service and Privacy Policy are being prepared
              with legal counsel. For questions in the meantime, contact{" "}
              <a
                href="mailto:support@informedaccelerator.com"
                className="text-v2-accent hover:text-v2-accentStrong underline underline-offset-2"
              >
                support@informedaccelerator.com
              </a>
              .
            </p>
          </div>
        </div>

        <Section id="introduction" title="1. Introduction">
          <p>
            Informed OS ("we", "us", "our") provides an educational
            funding readiness and credit improvement platform. This Privacy
            Policy explains what personal information we collect from you when
            you use our platform, how we use it, and the choices you have
            about that information.
          </p>
          <p>
            By using the platform you consent to the practices described here.
            If you do not agree with any part of this policy, please do not
            use the platform.
          </p>
        </Section>

        <Section id="information-we-collect" title="2. Information We Collect">
          <p>
            We collect information you provide directly (your name, email
            address, business details, and financial profile inputs), and
            information you upload (credit reports, bank statements, or other
            documents you choose to share with the platform for analysis).
          </p>
          <p>
            We also collect limited technical information about your session
            (IP address, device type, and platform events) so we can operate,
            secure, and improve the service.
          </p>
        </Section>

        <Section id="how-we-use" title="3. How We Use Information">
          <p>
            We use your information to compute your funding and mortgage
            readiness scores, generate educational analysis and draft
            documents, match you with relevant funding partners you may
            qualify for, and communicate with you about your account and any
            support requests you initiate.
          </p>
          <p>
            We do not sell your personal information. We do not share your
            uploaded credit report data with funding partners without your
            explicit consent on a per-match basis.
          </p>
        </Section>

        <Section id="sharing" title="4. Sharing &amp; Disclosure">
          <p>
            We share information only with the third-party service providers
            we rely on to operate the platform (for example, payment
            processing, email delivery, and secure document storage), each of
            which is contractually bound to protect your data and use it only
            for the purposes we specify.
          </p>
          <p>
            When you take an explicit action inside the platform to apply for
            or connect with a funding partner, we may share the profile data
            you have chosen to share with that partner. You review and
            approve this before it happens.
          </p>
        </Section>

        <Section id="retention" title="5. Data Retention">
          <p>
            Uploaded credit reports are retained for up to 30 days from the
            date of upload, then automatically purged from active storage.
            Analysis outputs (readiness scores, draft dispute letters, and
            partner matches) are retained on your account until you delete
            them or close your account.
          </p>
          <p>
            You can request deletion of your account and associated data at
            any time by contacting support.
          </p>
        </Section>

        <Section id="your-rights" title="6. Your Rights">
          <p>
            Depending on your jurisdiction, you may have rights to access,
            correct, export, or delete the personal information we hold about
            you. You can exercise most of these rights directly from your
            account settings, or by contacting us.
          </p>
        </Section>

        <Section id="contact" title="7. Contact">
          <p>
            Questions about this Privacy Policy? Reach us at{" "}
            <a
              href="mailto:support@informedaccelerator.com"
              className="text-v2-accent hover:text-v2-accentStrong underline underline-offset-2"
            >
              support@informedaccelerator.com
            </a>
            . We aim to respond within five business days.
          </p>
        </Section>
      </div>
    </main>
  );
}
